// Admin card — YABBR reply-flow health: poll status, webhook heartbeat,
// last reply received, one-click "Pull now" + SMS loopback test.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RadioTower, RefreshCw, Loader2, Send, CheckCircle2, Clock, Copy } from 'lucide-react';
import { YabbrCredentialsEditor } from './YabbrCredentialsEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ago = (iso) => {
  if (!iso) return 'never';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

const staleDays = (iso, days) => !iso || (Date.now() - new Date(iso).getTime()) > days * 86400000;

const WebhookMonitor = () => {
  const [listening, setListening] = useState(false);
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState(null);
  const sinceRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = async () => {
    try {
      const r = await axios.get(`${API}/admin/yabbr/webhook-activity`, { params: { since: sinceRef.current } });
      setMeta({ total: r.data.total_hits_ever, last: r.data.last_hit_ever });
      const evts = [
        ...(r.data.hits || []).map((h) => ({ kind: 'hit', at: h.received_at, outcome: h.outcome, reason: h.reason, parsed: h.parsed, raw: h.payload_sample })),
        ...(r.data.rejects || []).map((x) => ({ kind: 'reject', at: x.at, path: x.path, body: x.body_sample })),
      ].sort((a, b) => (a.at < b.at ? 1 : -1));
      setEvents(evts);
    } catch { /* keep polling */ }
  };

  const toggle = () => {
    if (listening) { clearInterval(pollRef.current); setListening(false); return; }
    sinceRef.current = new Date().toISOString();
    setEvents([]);
    setListening(true);
    poll();
    pollRef.current = setInterval(poll, 5000);
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-white/10" data-testid="yabbr-webhook-monitor">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-[10px] uppercase opacity-50">Live webhook monitor</p>
        <button onClick={toggle}
          className={`text-[11px] font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${listening ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-slate-800 dark:bg-white/10 hover:bg-slate-700 text-white'}`}
          data-testid="yabbr-webhook-monitor-toggle">
          {listening ? 'Stop' : 'Start listening'}
        </button>
        {listening && events.length === 0 && (
          <span className="text-xs text-teal-600 inline-flex items-center gap-1.5" data-testid="yabbr-webhook-monitor-waiting">
            <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
            Listening — reply to an SMS from your phone now…
          </span>
        )}
        {meta && (
          <span className="text-[10px] opacity-50" data-testid="yabbr-webhook-monitor-meta">
            {meta.total === 0 ? 'YABBR has NEVER hit this endpoint' : `${meta.total} hits total · last ${ago(meta.last)}`}
          </span>
        )}
      </div>
      {listening && events.length > 0 && (
        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="yabbr-webhook-monitor-events">
          {events.map((e, i) => e.kind === 'hit' ? (
            <div key={i}>
              <p className={`text-[11px] ${e.outcome === 'inbound_stored' ? 'text-emerald-600 font-semibold' : 'text-slate-600 dark:text-slate-300'}`}>
                ✓ {ago(e.at)} — <b>{e.outcome}</b>{e.reason ? ` (${e.reason})` : ''}
                {e.parsed?.from ? ` · from ${e.parsed.from}` : ''}{e.parsed?.content ? ` · “${e.parsed.content.slice(0, 50)}”` : ''}
              </p>
              {e.raw && (
                <p className="text-[10px] font-mono text-amber-700 dark:text-amber-400 break-all pl-4" data-testid="yabbr-webhook-raw-payload">
                  raw: {e.raw.slice(0, 220)}
                </p>
              )}
            </div>
          ) : (
            <p key={i} className="text-[11px] text-red-600">
              ⛔ {ago(e.at)} — REJECTED at {e.path} (wrong/missing secret){e.body ? ` · body: ${String(e.body).slice(0, 60)}` : ''}
            </p>
          ))}
        </div>
      )}
      {meta && meta.total === 0 && (
        <p className="text-[11px] text-amber-600 mt-1.5" data-testid="yabbr-webhook-never-hit-warning">
          ⚠ YABBR's POSTback URL is registered <b>per API key</b> in the YABBR portal (go.yabbr.io). If you rotated your API key,
          the webhook stays attached to the OLD key — re-register the postback URL against the key you're using NOW, then reply to an SMS while listening here.
        </p>
      )}
    </div>
  );
};

const LoopbackTest = () => {
  const [phone, setPhone] = useState('');
  const [test, setTest] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const start = async () => {
    setSending(true);
    setSendError(null);
    try {
      const r = await axios.post(`${API}/admin/yabbr/loopback-test`, { to_phone: phone });
      setTest({ ...r.data, reply: null });
      toast.success(`Test SMS sent to ${r.data.to_phone} — reply OK from that phone`);
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/yabbr/loopback-test/${r.data.test_id}`);
          setTest(s.data);
          if (s.data.reply) { clearInterval(pollRef.current); toast.success('Loopback reply received — SMS loop is healthy!'); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Test send failed';
      setSendError(detail);
      toast.error(detail, { duration: 10000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-white/10" data-testid="yabbr-loopback-section">
      <p className="text-[10px] uppercase opacity-50 mb-1.5">Loopback test — verify the whole SMS loop</p>
      <div className="flex gap-2 flex-wrap items-center">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your mobile e.g. 04xx xxx xxx"
          className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10 bg-transparent w-52"
          data-testid="yabbr-loopback-phone-input" />
        <button onClick={start} disabled={sending || !phone.trim()}
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-800 dark:bg-white/10 hover:bg-slate-700 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid="yabbr-loopback-send-btn">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send test SMS
        </button>
        {test && !test.reply && (
          <span className="text-xs text-amber-600 inline-flex items-center gap-1" data-testid="yabbr-loopback-waiting">
            <Clock className="w-3 h-3" /> Sent {ago(test.sent_at)}{test.delivery_status ? ` · ${test.delivery_status}` : ''} — waiting for your reply…
          </span>
        )}
        {test?.reply && (
          <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1" data-testid="yabbr-loopback-success">
            <CheckCircle2 className="w-3 h-3" /> Reply received {ago(test.reply.received_at)} via {test.reply.source}: “{test.reply.content?.slice(0, 40)}”
          </span>
        )}
      </div>
      {test?.yabbr && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 font-mono" data-testid="yabbr-loopback-raw-response">
          YABBR API → HTTP {test.yabbr.api_status} · {test.yabbr.status || '—'} · msg id {test.yabbr.message_id || 'none'} · sent from {test.yabbr.from} ({test.yabbr.source} creds)
        </p>
      )}
      {sendError && (
        <p className="text-[11px] text-red-600 mt-1.5 font-mono" data-testid="yabbr-loopback-error">
          ⛔ Raw error: {sendError}
        </p>
      )}
    </div>
  );
};

export const YabbrPollStatusCard = () => {
  const [data, setData] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [webhookPath, setWebhookPath] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/yabbr/poll-status`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    axios.get(`${API}/admin/yabbr/webhook-url`)
      .then((r) => setWebhookPath(r.data.path))
      .catch(() => {}); // non-master admins: button simply hidden
  }, []);

  const copyWebhookUrl = async () => {
    const url = `${window.location.origin}${webhookPath}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Webhook URL copied — paste it into the YABBR portal (POST, JSON)');
    } catch {
      toast.info(url);
    }
  };

  const pullNow = async () => {
    setPulling(true);
    try {
      const r = await axios.post(`${API}/admin/yabbr/pull-replies`, { hours: 168 });
      toast.success(`Pulled ${r.data.fetched} messages from the last 7 days — ${r.data.inbound_new} new repl${r.data.inbound_new === 1 ? 'y' : 'ies'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pull failed');
    } finally {
      setPulling(false);
    }
  };

  const last = data?.last;
  const webhook = data?.webhook;
  const lastInbound = data?.last_inbound;
  const healthy = last?.ok && !(last?.errors || []).length;
  const webhookSilent = !webhook?.last_at || staleDays(webhook.last_at, 2);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 p-4" data-testid="yabbr-poll-status-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <p className="font-semibold flex items-center gap-2">
          <RadioTower className="w-4 h-4 text-teal-600" /> SMS reply flow
          <span className={`w-2 h-2 rounded-full ${last ? (healthy ? 'bg-emerald-500' : 'bg-amber-500') : 'bg-slate-300'}`} />
        </p>
        <button onClick={pullNow} disabled={pulling}
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="yabbr-pull-now-btn">
          {pulling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Pull last 7 days
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm" data-testid="yabbr-poll-stats">
        <div><p className="text-[10px] uppercase opacity-50">Last reply received</p>
          <p className={`font-bold ${!lastInbound ? 'text-amber-600' : ''}`} data-testid="yabbr-last-reply">{ago(lastInbound?.received_at)}</p></div>
        <div><p className="text-[10px] uppercase opacity-50">Last webhook hit</p>
          <p className={`font-bold ${webhookSilent ? 'text-amber-600' : ''}`} data-testid="yabbr-last-webhook">{ago(webhook?.last_at)}</p></div>
        <div><p className="text-[10px] uppercase opacity-50">Last pull</p>
          <p className="font-bold" data-testid="yabbr-poll-last-run">{last ? ago(last.ran_at) : 'never'}</p></div>
        <div><p className="text-[10px] uppercase opacity-50">Messages seen</p>
          <p className="font-bold">{last?.fetched ?? 0}</p></div>
        <div><p className="text-[10px] uppercase opacity-50">New replies</p>
          <p className="font-bold">{last?.inbound_new ?? 0}</p></div>
        <div><p className="text-[10px] uppercase opacity-50">Receipts stamped</p>
          <p className="font-bold">{last?.receipts_updated ?? 0}</p></div>
      </div>
      {webhookSilent && (
        <p className="text-[11px] text-amber-600 mt-2" data-testid="yabbr-webhook-warning">
          ⚠ YABBR hasn't POSTed a webhook {webhook?.last_at ? `since ${ago(webhook.last_at)}` : 'yet'} — replies only arrive via the 2-min poll.
          Register the postback URL on your YABBR API key to restore real-time delivery.
        </p>
      )}
      {webhook?.rejects_24h > 0 && (
        <p className="text-[11px] text-red-600 mt-1" data-testid="yabbr-webhook-rejects">
          ⛔ {webhook.rejects_24h} webhook call{webhook.rejects_24h === 1 ? '' : 's'} REJECTED in the last 24h (wrong path or secret) — something IS posting to the app but failing auth. Re-copy the webhook URL below and paste it exactly.
        </p>
      )}
      {webhookPath && (
        <button onClick={copyWebhookUrl}
          className="text-[11px] font-bold mt-2 px-3 py-1.5 rounded-full border border-teal-600 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-500/10 inline-flex items-center gap-1.5"
          data-testid="yabbr-copy-webhook-url-btn">
          <Copy className="w-3 h-3" /> Copy webhook URL for the YABBR portal
        </button>
      )}
      {(last?.errors || []).length > 0 && (
        <p className="text-[11px] text-red-600 mt-2" data-testid="yabbr-poll-errors">⚠ {last.errors.join(' · ')}</p>
      )}
      <LoopbackTest />
      <WebhookMonitor />
      <YabbrCredentialsEditor />
      <p className="text-[10px] opacity-40 mt-2">Replies are pulled straight from the YABBR API every 2 minutes as a safety net for the webhook. Use the loopback test after changing YABBR credentials or webhook settings.</p>
    </div>
  );
};
